import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Save, RefreshCw, Send, Check, AlertTriangle, Mail } from 'lucide-react';
import { neonQuery } from '../../services/api';
import { getActiveEnv } from '../../services/env';
import { getCurrentUser } from '../../services/auth';

// ── Site-wide "Send me this Recipe" email config ─────────────────────────────
// Backs the recipe-email.js Netlify function. The per-product button (which
// photos, which recipe) is configured on the PDP tab → Recipe Button section;
// this tab controls the one shared email template + the Dotdigital list every
// requester is added to. Stored as a single JSON row via recipe_email_config_*.

const FN_BASE = window.location.hostname === 'localhost'
  ? 'http://localhost:8888/.netlify/functions'
  : '/.netlify/functions';

// Mirror of CONFIG_DEFAULTS in netlify/functions/recipe-email.js — keep in sync.
const DEFAULTS = {
  from_name: 'The Crab Place Kitchen',
  from_email: 'customerservice@crabplace.com',
  reply_to: '',
  subject: "Here's your {recipe} recipe! 🦀",
  intro_html: '<p>Thanks for asking! Here is the recipe straight from our kitchen in Crisfield, Maryland. We hope it makes your next seafood feast unforgettable.</p>',
  footer_html: '<p>Happy cooking,<br>The Crab Place Team</p><p style="font-size:12px;color:#888;">You are receiving this because you requested a recipe at crabplace.com. We will also send you the occasional recipe and offer — you can unsubscribe any time.</p>',
  cta_text: 'Shop Fresh Seafood',
  cta_url: 'https://www.crabplace.com',
  header_image_url: '',
  accent_color: '#DC582A',
  site_origin: 'https://www.crabplace.com',
  bcc_admin: '',
  dotdigital_enabled: true,
  dotdigital_address_book_id: '',
};

const lbl = { fontSize: 11, fontWeight: 700, color: 'var(--gray-600)', display: 'block', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '.4px' };
const inp = { width: '100%', padding: '8px 10px', border: '1px solid var(--gray-300)', borderRadius: 5, fontSize: 13, boxSizing: 'border-box', fontFamily: 'inherit' };
const hint = { fontSize: 11, color: 'var(--gray-500)', marginTop: 3 };
const card = { background: '#fff', border: '1px solid var(--gray-200)', borderRadius: 8, padding: 18, marginBottom: 16 };
const cardTitle = { fontSize: 13, fontWeight: 700, color: 'var(--navy)', margin: '0 0 14px', textTransform: 'uppercase', letterSpacing: '.5px' };

export default function SiteContentRecipeEmailTab() {
  const [cfg, setCfg] = useState(DEFAULTS);
  const [loaded, setLoaded] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null); // { type, msg }

  // test send
  const [recipes, setRecipes] = useState([]);
  const [testRecipe, setTestRecipe] = useState('');
  const [testEmail, setTestEmail] = useState(getCurrentUser()?.email || '');
  const [testing, setTesting] = useState(false);

  // recent sends
  const [log, setLog] = useState([]);
  const [logStats, setLogStats] = useState({});

  const flash = (type, msg) => {
    setStatus({ type, msg });
    if (type !== 'error') setTimeout(() => setStatus(null), 4000);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await neonQuery('recipe_email_config_get', { env: getActiveEnv() });
      const merged = { ...DEFAULTS, ...((res && res.config) || {}) };
      setCfg(merged);
      setLoaded(merged);
    } catch (e) {
      flash('error', `Could not load settings: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadLog = useCallback(async () => {
    try {
      const res = await neonQuery('recipe_email_log_list', { limit: 25, env: getActiveEnv() });
      setLog(Array.isArray(res?.rows) ? res.rows : []);
      setLogStats(res?.stats || {});
    } catch { /* log panel just stays empty */ }
  }, []);

  useEffect(() => {
    load();
    loadLog();
    neonQuery('recipes_list', { limit: 200, visible: true, sort: 'name' })
      .then(r => {
        const list = Array.isArray(r?.data) ? r.data : [];
        setRecipes(list);
        if (list[0]) setTestRecipe(list[0].slug);
      })
      .catch(() => {});
  }, [load, loadLog]);

  const dirty = useMemo(() => JSON.stringify(cfg) !== JSON.stringify(loaded), [cfg, loaded]);
  const set = (key, value) => setCfg(prev => ({ ...prev, [key]: value }));

  const save = async () => {
    setSaving(true);
    setStatus(null);
    try {
      const res = await neonQuery('recipe_email_config_set', { config: cfg, env: getActiveEnv() });
      const merged = { ...DEFAULTS, ...((res && res.config) || cfg) };
      setLoaded(merged);
      setCfg(merged);
      flash('success', 'Recipe email settings saved.');
    } catch (e) {
      flash('error', `Save failed: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    if (!testEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(testEmail)) {
      flash('error', 'Enter a valid email address for the test send.');
      return;
    }
    if (!testRecipe) {
      flash('error', 'Pick a recipe to preview.');
      return;
    }
    if (dirty) {
      flash('error', 'Save your changes first — the test uses the saved settings.');
      return;
    }
    setTesting(true);
    setStatus(null);
    try {
      const res = await fetch(`${FN_BASE}/recipe-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ test: true, email: testEmail, recipeSlug: testRecipe }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.ok) {
        flash('success', `Test recipe email sent to ${testEmail}.`);
        loadLog();
      } else {
        flash('error', data.error || `Test send failed (HTTP ${res.status}).`);
      }
    } catch (e) {
      flash('error', `Test send failed: ${e.message}`);
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: 'var(--gray-500)' }}>
        <RefreshCw size={20} className="spin" style={{ verticalAlign: 'middle' }} /> Loading recipe email settings…
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
        <p style={{ margin: 0, fontSize: 12.5, color: 'var(--gray-600)', maxWidth: 640 }}>
          The shared template for every “Send me this Recipe” email. The recipe content (ingredients, steps, photo)
          is filled in automatically — these settings control the wrapper around it and the Dotdigital list each
          requester joins. Turn the button on per product in the <strong>PDP</strong> tab → Recipe Button.
        </p>
        <button
          onClick={save}
          disabled={!dirty || saving}
          style={{
            padding: '9px 18px', background: dirty ? 'var(--orange)' : 'var(--gray-300)', color: '#fff',
            border: 'none', borderRadius: 5, cursor: dirty && !saving ? 'pointer' : 'not-allowed',
            fontSize: 13, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap',
          }}
        >
          {saving ? <RefreshCw size={14} className="spin" /> : <Save size={14} />}
          {saving ? 'Saving…' : dirty ? 'Save Settings' : 'Saved'}
        </button>
      </div>

      {status && (
        <div style={{
          padding: '9px 12px', borderRadius: 6, fontSize: 12.5, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 7,
          background: status.type === 'error' ? '#fef2f2' : '#ecfdf5',
          border: `1px solid ${status.type === 'error' ? '#fecaca' : '#a7f3d0'}`,
          color: status.type === 'error' ? '#b91c1c' : '#065f46',
        }}>
          {status.type === 'error' ? <AlertTriangle size={14} /> : <Check size={14} />}
          {status.msg}
        </div>
      )}

      {/* Email content */}
      <div style={card}>
        <h3 style={cardTitle}>Email Content</h3>
        <div style={{ marginBottom: 12 }}>
          <label style={lbl}>Subject line</label>
          <input style={inp} value={cfg.subject} onChange={e => set('subject', e.target.value)} placeholder={DEFAULTS.subject} />
          <div style={hint}>Use <code>{'{recipe}'}</code> where you want the recipe name to appear.</div>
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={lbl}>Intro message <span style={{ textTransform: 'none', fontWeight: 400, color: 'var(--gray-400)' }}>(appears above the recipe)</span></label>
          <textarea style={{ ...inp, minHeight: 80, resize: 'vertical' }} value={cfg.intro_html} onChange={e => set('intro_html', e.target.value)} placeholder={DEFAULTS.intro_html} />
          <div style={hint}>Plain text or simple HTML.</div>
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={lbl}>Footer / sign-off <span style={{ textTransform: 'none', fontWeight: 400, color: 'var(--gray-400)' }}>(appears below the recipe)</span></label>
          <textarea style={{ ...inp, minHeight: 80, resize: 'vertical' }} value={cfg.footer_html} onChange={e => set('footer_html', e.target.value)} placeholder={DEFAULTS.footer_html} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <label style={lbl}>Header image URL <span style={{ textTransform: 'none', fontWeight: 400, color: 'var(--gray-400)' }}>(optional banner)</span></label>
            <input style={inp} value={cfg.header_image_url} onChange={e => set('header_image_url', e.target.value)} placeholder="Blank = navy “Crab Place · Recipes” bar" />
          </div>
          <div>
            <label style={lbl}>Accent color</label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input type="color" value={/^#[0-9a-f]{6}$/i.test(cfg.accent_color) ? cfg.accent_color : '#DC582A'} onChange={e => set('accent_color', e.target.value)} style={{ width: 44, height: 36, padding: 2, border: '1px solid var(--gray-300)', borderRadius: 5, cursor: 'pointer' }} />
              <input style={{ ...inp, flex: 1 }} value={cfg.accent_color} onChange={e => set('accent_color', e.target.value)} placeholder="#DC582A" />
            </div>
          </div>
        </div>
      </div>

      {/* Sender & links */}
      <div style={card}>
        <h3 style={cardTitle}>Sender &amp; Links</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
          <div>
            <label style={lbl}>From name</label>
            <input style={inp} value={cfg.from_name} onChange={e => set('from_name', e.target.value)} placeholder={DEFAULTS.from_name} />
          </div>
          <div>
            <label style={lbl}>From email address</label>
            <input style={inp} value={cfg.from_email} onChange={e => set('from_email', e.target.value)} placeholder={DEFAULTS.from_email} />
            <div style={hint}>Must be a <strong>@crabplace.com</strong> address — that domain is verified in Resend. Other domains will be rejected.</div>
          </div>
          <div>
            <label style={lbl}>Reply-to <span style={{ textTransform: 'none', fontWeight: 400, color: 'var(--gray-400)' }}>(optional)</span></label>
            <input style={inp} value={cfg.reply_to} onChange={e => set('reply_to', e.target.value)} placeholder="customerservice@crabplace.com" />
          </div>
          <div>
            <label style={lbl}>BCC admin copy <span style={{ textTransform: 'none', fontWeight: 400, color: 'var(--gray-400)' }}>(optional)</span></label>
            <input style={inp} value={cfg.bcc_admin} onChange={e => set('bcc_admin', e.target.value)} placeholder="greg@crabplace.com" />
          </div>
          <div>
            <label style={lbl}>“Shop” button text</label>
            <input style={inp} value={cfg.cta_text} onChange={e => set('cta_text', e.target.value)} placeholder={DEFAULTS.cta_text} />
          </div>
          <div>
            <label style={lbl}>“Shop” button link</label>
            <input style={inp} value={cfg.cta_url} onChange={e => set('cta_url', e.target.value)} placeholder={DEFAULTS.cta_url} />
          </div>
          <div>
            <label style={lbl}>Storefront address</label>
            <input style={inp} value={cfg.site_origin} onChange={e => set('site_origin', e.target.value)} placeholder={DEFAULTS.site_origin} />
            <div style={hint}>Used to build the “View this recipe online” link.</div>
          </div>
        </div>
      </div>

      {/* Dotdigital */}
      <div style={card}>
        <h3 style={cardTitle}>Dotdigital List</h3>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 13, marginBottom: 12 }}>
          <input type="checkbox" checked={cfg.dotdigital_enabled !== false} onChange={e => set('dotdigital_enabled', e.target.checked)} style={{ accentColor: 'var(--orange)' }} />
          Add every recipe requester to a Dotdigital address book
        </label>
        <div style={{ maxWidth: 320 }}>
          <label style={lbl}>Dotdigital address book ID</label>
          <input style={inp} value={cfg.dotdigital_address_book_id} onChange={e => set('dotdigital_address_book_id', e.target.value)} placeholder="e.g. 123456" />
          <div style={hint}>
            Numeric ID of the address book (Dotdigital → Audience → Lists). Recipe signups use the
            <strong> same Dotdigital connection as Email Campaigns</strong> — no extra setup. Until an
            address book ID is entered here the recipe email still sends; the signup is just skipped.
          </div>
        </div>
      </div>

      {/* Test send */}
      <div style={card}>
        <h3 style={cardTitle}>Send a Test</h3>
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div style={{ minWidth: 220 }}>
            <label style={lbl}>Recipe to preview</label>
            <select style={{ ...inp, background: '#fff' }} value={testRecipe} onChange={e => setTestRecipe(e.target.value)}>
              {recipes.length === 0 && <option value="">No recipes found</option>}
              {recipes.map(r => <option key={r.slug} value={r.slug}>{r.name}</option>)}
            </select>
          </div>
          <div style={{ minWidth: 220 }}>
            <label style={lbl}>Send to</label>
            <input style={inp} value={testEmail} onChange={e => setTestEmail(e.target.value)} placeholder="you@crabplace.com" />
          </div>
          <button
            onClick={sendTest}
            disabled={testing}
            style={{
              padding: '9px 16px', background: 'var(--navy)', color: '#fff', border: 'none', borderRadius: 5,
              cursor: testing ? 'wait' : 'pointer', fontSize: 13, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6,
            }}
          >
            {testing ? <RefreshCw size={14} className="spin" /> : <Send size={14} />}
            {testing ? 'Sending…' : 'Send Test'}
          </button>
        </div>
        <div style={{ ...hint, marginTop: 8 }}>A test send uses the saved settings and skips the Dotdigital signup.</div>
      </div>

      {/* Recent sends */}
      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <h3 style={{ ...cardTitle, margin: 0 }}>Recent Sends</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 11.5, color: 'var(--gray-500)' }}>
              {logStats.total || 0} total · {logStats.last30 || 0} in 30 days · {logStats.unique_emails || 0} unique emails
            </span>
            <button onClick={loadLog} title="Refresh" style={{ background: 'none', border: '1px solid var(--gray-300)', borderRadius: 5, padding: '5px 8px', cursor: 'pointer', color: 'var(--navy)' }}>
              <RefreshCw size={13} />
            </button>
          </div>
        </div>
        {log.length === 0 ? (
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--gray-500)', fontSize: 13, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
            <Mail size={20} style={{ opacity: 0.5 }} />
            No recipe emails sent yet.
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ textAlign: 'left', color: 'var(--gray-500)', borderBottom: '1px solid var(--gray-200)' }}>
                  <th style={{ padding: '6px 8px' }}>When</th>
                  <th style={{ padding: '6px 8px' }}>Email</th>
                  <th style={{ padding: '6px 8px' }}>Recipe</th>
                  <th style={{ padding: '6px 8px' }}>Status</th>
                  <th style={{ padding: '6px 8px' }}>Dotdigital</th>
                </tr>
              </thead>
              <tbody>
                {log.map(row => (
                  <tr key={row.id} style={{ borderBottom: '1px solid var(--gray-100)' }}>
                    <td style={{ padding: '6px 8px', whiteSpace: 'nowrap', color: 'var(--gray-500)' }}>
                      {row.created_at ? new Date(row.created_at).toLocaleString() : ''}
                    </td>
                    <td style={{ padding: '6px 8px' }}>{row.email}</td>
                    <td style={{ padding: '6px 8px' }}>{row.recipe_name || row.recipe_slug || '—'}</td>
                    <td style={{ padding: '6px 8px' }}>
                      <StatusPill value={row.status} error={row.error} />
                    </td>
                    <td style={{ padding: '6px 8px', color: 'var(--gray-500)' }}>{row.dotdigital_status || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function StatusPill({ value, error }) {
  const v = String(value || '').toLowerCase();
  const ok = v === 'sent';
  const test = v === 'test';
  const bg = ok ? '#ecfdf5' : test ? '#eff6ff' : '#fef2f2';
  const fg = ok ? '#065f46' : test ? '#1e40af' : '#b91c1c';
  return (
    <span title={error || ''} style={{ background: bg, color: fg, borderRadius: 999, padding: '2px 9px', fontSize: 11, fontWeight: 700 }}>
      {value || 'sent'}
    </span>
  );
}
